import {
    useAddNewPropertyAmenityMutation,
    useDeletePropertyAmenityMutation,
    useEditPropertyAmenityMutation,
    useLazyGetPropertyAmenitiesQuery,
} from "../../services/bClientApi"

export type AmenityCopyMode = 'add-missing' | 'replace-matching' | 'replace-all'

export const AMENITY_COPY_MODE_OPTIONS: { value: AmenityCopyMode, label: string, description: string }[] = [
    {
        value: 'add-missing',
        label: 'Add missing only',
        description: 'Adds amenities the target does not have. Amenities it already has are left untouched.',
    },
    {
        value: 'replace-matching',
        label: 'Add missing and replace matching',
        description: 'Adds missing amenities and overwrites the overrides of ones the target already has. Other target amenities are kept.',
    },
    {
        value: 'replace-all',
        label: 'Replace all',
        description: 'Deletes every amenity assigned to the target, then creates the copied ones.',
    },
]

export interface PropertyAmenityDoc {
    _id: string
    propertyId: string
    amenityId: string
    overrides?: any
}

export interface AmenityCopyPlan {
    propertyId: string
    toAdd: { amenityId: string, overrides: any }[]
    toUpdate: { _id: string, amenityId: string, overrides: any }[]
    toDelete: { _id: string, amenityId: string }[]
    error?: string
}

export interface AmenityCopyResult {
    propertyId: string
    added: number
    updated: number
    deleted: number
    failed: number
}

// Empty selectedAmenityIds means every source amenity is copied
export function buildAmenityCopyPlan(
    sourceAmenities: PropertyAmenityDoc[],
    targetAmenities: PropertyAmenityDoc[],
    targetPropertyId: string,
    mode: AmenityCopyMode,
    selectedAmenityIds: string[],
): AmenityCopyPlan {
    const selected = selectedAmenityIds.length
        ? sourceAmenities.filter((pa) => selectedAmenityIds.includes(pa.amenityId))
        : sourceAmenities
    const uniqueSource = Array.from(new Map(selected.map((pa) => [pa.amenityId, pa])).values())

    const plan: AmenityCopyPlan = { propertyId: targetPropertyId, toAdd: [], toUpdate: [], toDelete: [] }

    if (mode === 'replace-all') {
        plan.toDelete = targetAmenities.map((pa) => ({ _id: pa._id, amenityId: pa.amenityId }))
        plan.toAdd = uniqueSource.map((pa) => ({ amenityId: pa.amenityId, overrides: pa.overrides || {} }))
        return plan
    }

    const existingByAmenityId = new Map<string, PropertyAmenityDoc>()
    targetAmenities.forEach((pa) => {
        if (!existingByAmenityId.has(pa.amenityId)) existingByAmenityId.set(pa.amenityId, pa)
    })

    uniqueSource.forEach((pa) => {
        const overrides = pa.overrides || {}
        const existing = existingByAmenityId.get(pa.amenityId)
        if (!existing) {
            plan.toAdd.push({ amenityId: pa.amenityId, overrides })
        } else if (
            mode === 'replace-matching' &&
            JSON.stringify(existing.overrides || {}) !== JSON.stringify(overrides)
        ) {
            plan.toUpdate.push({ _id: existing._id, amenityId: pa.amenityId, overrides })
        }
    })

    return plan
}

export function formatAmenityCopyPlan(plan: AmenityCopyPlan) {
    if (plan.error) return plan.error
    return `${plan.toAdd.length} to add, ${plan.toUpdate.length} to update, ${plan.toDelete.length} to delete`
}

export function formatAmenityCopyResult(result: AmenityCopyResult) {
    const failedText = result.failed > 0 ? `, ${result.failed} failed` : ''
    return `${result.added} added, ${result.updated} updated, ${result.deleted} deleted${failedText}`
}

export default function usePropertyAmenityCopy() {
    const [getPropertyAmenities] = useLazyGetPropertyAmenitiesQuery()
    const [addNewPropertyAmenity] = useAddNewPropertyAmenityMutation()
    const [editPropertyAmenity] = useEditPropertyAmenityMutation()
    const [deletePropertyAmenity] = useDeletePropertyAmenityMutation()

    // Always reads the target's current amenities so the plan reflects the database
    const planCopy = async (
        sourceAmenities: PropertyAmenityDoc[],
        targetPropertyId: string,
        mode: AmenityCopyMode,
        selectedAmenityIds: string[],
    ): Promise<AmenityCopyPlan> => {
        try {
            const targetData = await getPropertyAmenities({
                propertyAmenitiesQuery: { propertyId: targetPropertyId },
                pagination: { page: 1, limit: 500, all: true },
            }).unwrap()
            return buildAmenityCopyPlan(sourceAmenities, targetData?.docs || [], targetPropertyId, mode, selectedAmenityIds)
        } catch (err) {
            console.error('Failed to load property amenities:', err)
            return {
                propertyId: targetPropertyId,
                toAdd: [],
                toUpdate: [],
                toDelete: [],
                error: 'Could not load the current amenities',
            }
        }
    }

    // Deletes run before adds so a "replace all" never ends up with duplicates
    const executePlan = async (plan: AmenityCopyPlan): Promise<AmenityCopyResult> => {
        const result: AmenityCopyResult = { propertyId: plan.propertyId, added: 0, updated: 0, deleted: 0, failed: 0 }
        if (plan.error) {
            result.failed = 1
            return result
        }

        for (const entry of plan.toDelete) {
            try {
                await deletePropertyAmenity(entry._id).unwrap()
                result.deleted++
            } catch (err) {
                console.error('Failed to delete property amenity:', err)
                result.failed++
            }
        }
        for (const entry of plan.toAdd) {
            try {
                await addNewPropertyAmenity({
                    propertyId: plan.propertyId,
                    amenityId: entry.amenityId,
                    overrides: entry.overrides,
                }).unwrap()
                result.added++
            } catch (err) {
                console.error('Failed to add property amenity:', err)
                result.failed++
            }
        }
        for (const entry of plan.toUpdate) {
            try {
                await editPropertyAmenity({
                    propertyAmenityId: entry._id,
                    propertyAmenityInfo: { overrides: entry.overrides },
                }).unwrap()
                result.updated++
            } catch (err) {
                console.error('Failed to update property amenity:', err)
                result.failed++
            }
        }
        return result
    }

    return { planCopy, executePlan }
}
